import { generatePath, useParams } from '@fs/zion-router'
import { type PID } from '@fs/zion-tree-types'
import { pidRegexStr } from '@fs/zion-pid'
import { type PathParamMap } from '../utils/pathParamTypes'

export const PERSON_SUBPAGES = [
  'details',
  'ordinances',
  'about',
  'sources',
  'memories',
  'vitals',
  'non-vitals',
  'family',
  'tools',
  'search-records',
  'research-help',
  'collaborate',
  'timeline',
] as const

export type PersonSubPage = (typeof PERSON_SUBPAGES)[number]

export const PERSON_ROUTE_PATH =
  `/:subPage(${PERSON_SUBPAGES.join('|') as PersonSubPage})/:pid(${pidRegexStr as PID})` as const

export const ORDINANCES_FAMILY_TAB_PATHS = ['spouses-children', 'parents-siblings'] as const

export type OrdinancesFamilyTabPath = (typeof ORDINANCES_FAMILY_TAB_PATHS)[number]

export const ORDINANCES_FAMILY_TAB_ROUTE_PATH =
  `/:subPage(ordinances)/:pid(${pidRegexStr as PID})/:familyTab(${ORDINANCES_FAMILY_TAB_PATHS.join('|') as OrdinancesFamilyTabPath})` as const

export const PARTIAL_PERSON_ROUTE_PATHS = [
  `/:pid(${pidRegexStr as PID})`,
  `/:subPage(${PERSON_SUBPAGES.join('|') as PersonSubPage})`,
  '/',
] as const

type PersonFullRouteParams = PathParamMap<typeof PERSON_ROUTE_PATH>
type PersonPartialRouteParams = PathParamMap<typeof PARTIAL_PERSON_ROUTE_PATHS>

type PersonPageParams<Type> = Type extends 'full' ? PersonFullRouteParams : PersonPartialRouteParams

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- runtime arg exists solely for type narrowing
export function usePersonPageParams<Type extends 'full' | 'partial'>(_type: Type): PersonPageParams<Type> {
  return useParams<PersonPageParams<Type>>()
}

export function useOrdinancesFamilyTabParam(): OrdinancesFamilyTabPath | undefined {
  return useParams<Partial<PathParamMap<typeof ORDINANCES_FAMILY_TAB_ROUTE_PATH>>>().familyTab
}

export function generateOrdinancesFamilyTabUrl(pid: PID, familyTab?: OrdinancesFamilyTabPath): string {
  if (!familyTab) {
    return generatePersonPageUrl('ordinances', pid)
  }
  return generatePath(ORDINANCES_FAMILY_TAB_ROUTE_PATH, { subPage: 'ordinances', pid, familyTab })
}

export function generatePersonPageUrl(subPage: PersonSubPage, pid: PID, search = ''): string {
  return `${generatePath(PERSON_ROUTE_PATH, { subPage, pid })}${search}`
}
